/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {client, providers} from '@logilab/cwclientlibjs';

/**
 * The timeout for validating the user's input
 */
export const EDITION_TIMEOUT: number = 750;

/**
 * The type of a translation function
 */
export type TranslateFunction = (key: string, ...args: any[]) => string;

/**
 * The type of context for translations
 */
export interface TranslationContextType {
    /**
     * The translation function
     */
    translate: TranslateFunction;
}

/**
 * The default translation context
 */
export const TRANSLATION_CONTEXT_DEFAULT: TranslationContextType = {
    translate: (key: string, ..._args: any[]) => key,
};

/**
 * The context for the translation fonction
 */
export const TranslationContext = React.createContext(
    TRANSLATION_CONTEXT_DEFAULT
);

/**
 * The type of a context for entity forms
 */
export interface ProvidersContextType {
    /**
     * The provider of entity schemas
     */
    schemaProvider: providers.EntitySchemaProvider;
    /**
     * The provider of entities
     */
    entityProvider: providers.EntityProvider;
    /**
     * The RQL client to query the CubicWeb instance
     */
    rqlClient?: client.RqlClient;
}

const ProvidersContextDefault: ProvidersContextType = {
    schemaProvider: null as any, // should be provided
    entityProvider: null as any, // should be provided
};

/**
 * Context for entity forms
 */
export const ProvidersContext = React.createContext(ProvidersContextDefault);

/**
 * Represents an error when validating the new value of an attribute
 */
export interface ValidationError {
    /**
     * The message to be displayed
     */
    message: string;
    /**
     * The parameters for the message
     */
    parameters?: any[];
}

/**
 * Type of event handler for the update of the value of an entity's attribute
 * @param entity The entity that is updated
 * @param attribute The schema for the attribute
 * @param required Whether the attribute is required
 * @param value The value for the attribute
 * @returns A validation error, or null if the value was valid
 */
export type AttributeValueEvent = (
    entity: providers.Entity,
    attribute: providers.EntityAttributeSchema,
    required: boolean,
    value: any,
    rqlClient?: client.RqlClient
) => ValidationError | null;

/**
 * Default attribute validation
 * @param entity The entity that is updated
 * @param attribute The schema for the attribute
 * @param required Whether the attribute is required
 * @param value The value for the attribute
 * @returns A validation error, or null if the value was valid
 */
export function defaultAttributeValidation(
    entity: providers.Entity,
    attribute: providers.EntityAttributeSchema,
    required: boolean,
    value: any
): ValidationError | null {
    if (!required) {
        return null;
    }
    if (value === null || value === undefined) {
        return {message: 'Expected a value'};
    }
    return null;
}

/**
 * Builds a validation function for an integer entity attribute
 * @param min The minimum of the acceptable range
 * @param max The maximum of the acceptable range
 */
export function buildIntegerAttributeValidation(
    min: number | undefined,
    max: number | undefined
): AttributeValueEvent {
    const rangeMin = min === undefined ? Number.MIN_SAFE_INTEGER : min;
    const rangeMax = max === undefined ? Number.MAX_SAFE_INTEGER : max;
    return (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        required: boolean,
        value: any
    ) => {
        if (value === null || value === undefined) {
            if (required) {
                return {message: 'Expected a number'};
            } else {
                return null;
            }
        }
        if (value === '') {
            return {message: 'Not a number'};
        }
        try {
            const num = Number(value).valueOf();
            if (Number.isNaN(num)) {
                return {message: 'Not a number'};
            }
            if (!Number.isInteger(num)) {
                return {message: 'Not an integer number'};
            }
            if (num < rangeMin || num > rangeMax) {
                return {
                    message: 'Outside of range [{0},{1}]',
                    parameters: [rangeMin, rangeMax],
                };
            }
        } catch (error) {
            return {message: 'Not a number'};
        }
        return null;
    };
}

/**
 * Builds a validation function for a float entity attribute
 * @param min The minimum of the acceptable range
 * @param max The maximum of the acceptable range
 */
export function buildFloatAttributeValidation(
    min: number | undefined,
    max: number | undefined
): AttributeValueEvent {
    const rangeMin = min === undefined ? -Number.MAX_VALUE : min;
    const rangeMax = max === undefined ? Number.MAX_VALUE : max;
    return (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        required: boolean,
        value: any
    ) => {
        if (value === null || value === undefined) {
            if (required) {
                return {message: 'Expected a number'};
            } else {
                return null;
            }
        }
        if (value === '') {
            return {message: 'Not a number'};
        }
        try {
            const num = Number(value).valueOf();
            if (Number.isNaN(num)) {
                return {message: 'Not a number'};
            }
            if (num < rangeMin || num > rangeMax) {
                return {
                    message: 'Outside of range [{0},{1}]',
                    parameters: [rangeMin, rangeMax],
                };
            }
        } catch (error) {
            return {message: 'Not a number'};
        }
        return null;
    };
}

/**
 * A validation function that checks for a positive float
 */
export const POSITIVE_FLOAT_VALIDATION = buildFloatAttributeValidation(
    0,
    Number.MAX_VALUE
);

/**
 * A validation function that checks for a positive integer
 */
export const POSITIVE_INTEGER_VALIDATION = buildIntegerAttributeValidation(
    0,
    Number.MAX_SAFE_INTEGER
);

/**
 * A validation function that does not check anything
 * @param entity The entity that is updated
 * @param attribute The schema for the attribute
 * @param required Whether the attribute is required
 * @param value The value for the attribute
 * @returns A validation error, or null if the value was valid
 */
export function noValidate(
    _entity: providers.Entity,
    _attribute: providers.EntityAttributeSchema,
    _required: boolean,
    _value: any
): ValidationError | null {
    return null;
}

/**
 * Builds a validation function that cheks that a float value is within an interval
 * @param attributeMin The attribute for the min value
 * @param attributeMax The attribute for the max value
 */
export function buildValidateInterInMinMax(
    attributeMin: providers.EntityAttributeSchema,
    attributeMax: providers.EntityAttributeSchema
): AttributeValueEvent {
    return (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        required: boolean,
        value: any
    ) => {
        const error = POSITIVE_FLOAT_VALIDATION(
            entity,
            attribute,
            required,
            value
        );
        if (error !== null) {
            return error;
        }
        const min = entity[attributeMin.name];
        const max = entity[attributeMax.name];
        const inter = value as number;
        if (inter >= min && inter <= max) {
            return null;
        }
        return {
            message: `${attribute.name} must be within [${min}-${max}]`,
        };
    };
}

/**
 * Builds a validation function that cheks that a float is greater that another attribute
 * @param otherAttribute The other attribute
 */
export function buildValidatePositiveGreaterThan(
    otherAttribute: providers.EntityAttributeSchema
): AttributeValueEvent {
    return (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        required: boolean,
        value: any
    ) => {
        const error = POSITIVE_FLOAT_VALIDATION(
            entity,
            attribute,
            required,
            value
        );
        if (error !== null) {
            return error;
        }
        const other = entity[otherAttribute.name];
        const current = value as number;
        if (current >= other) {
            return null;
        }
        return {
            message: `${attribute.name} must be greater than ${other}`,
        };
    };
}
/**
 * Builds a validation function that cheks that a float is less that another attribute
 * @param otherAttribute The other attribute
 */
export function buildValidatePositiveLessThan(
    otherAttribute: providers.EntityAttributeSchema
): AttributeValueEvent {
    return (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        required: boolean,
        value: any
    ) => {
        const error = POSITIVE_FLOAT_VALIDATION(
            entity,
            attribute,
            required,
            value
        );
        if (error !== null) {
            return error;
        }
        const other = entity[otherAttribute.name];
        const current = value as number;
        if (current <= other) {
            return null;
        }
        return {
            message: `${attribute.name} must be less than ${other}`,
        };
    };
}

/**
 * Builds a validation function for an enumerable entity attribute
 * @param values The allowed values
 */
export function buildEnumAttributeValidation(
    values: any[]
): AttributeValueEvent {
    return (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        required: boolean,
        value: any
    ) => {
        if (value === null || value === undefined) {
            if (required) {
                return {message: 'Expected a value'};
            } else {
                return null;
            }
        }
        if (values.indexOf(value) < 0) {
            return {message: 'Value must be within enumeration'};
        }
        return null;
    };
}

/**
 * Gets the displayable text for a value
 * @param value The value to display
 * @returns The displayable text
 */
export function getDisplayText(value: any): string {
    if (value === null || value === undefined) {
        return '';
    } else {
        return value.toString();
    }
}

/**
 * Compares two types based on their names
 * @param namedEntity1 A named entity
 * @param namedEntity2 A named Entity
 * @returns 1,-1 or 0 to sort by descending alphabetical order
 */

export function compareNamedEntities(
    namedEntity1: {name: string},
    namedEntity2: {name: string}
) {
    return namedEntity1.name.localeCompare(namedEntity2.name);
}

/**
 * Retrieves an named entity in the list of object given its name
 * @param name The name of the entity looked for
 * @param entityList The list of named entities
 */
export function getNamedEntityFromList<NamedEntity extends {name: string}>(
    name: string,
    entityList: NamedEntity[]
): NamedEntity | null {
    const entity = entityList.find((entity) => entity.name === name);
    return entity ? entity : null;
}

/**
 * Save the new attribute value for an entity
 * @param entity The entity to modify
 * @param attribute The attribute to modify
 * @param required Whether the attribute is required
 * @param value The new value to set
 * @param rqlClient the RqlClient to use to set the new value
 */
export function saveNewAttributeValue(
    entity: providers.Entity,
    attribute: providers.EntityAttributeSchema,
    required: boolean,
    value: any,
    rqlClient?: client.RqlClient
): Promise<client.RqlIoV1TransactionResponse> {
    if (rqlClient === undefined) {
        return Promise.reject(
            'a rqlClient must be defined to change an attribute value'
        );
    }
    if (required && value === undefined) {
        return Promise.reject('Value is required');
    }
    return rqlClient.transactionV1([
        [
            `SET X ${attribute.name} %(value)s WHERE X eid %(eid)s`,
            {value, eid: entity['@eid']},
        ],
    ]);
}

/**
 * Determine if the attribute is type RichString, which means is type String and
 * the entity got another String attribute with [attribute.name]_format as name.
 * @param attribute The attribute to determine is a RichString
 * @param attributes (optional) The other attributes available
 * @returns boolean
 */
export function isRichString(
    attribute: providers.EntityAttributeSchema,
    attributes?: providers.EntityAttributeSchema[]
): boolean {
    return (
        attribute.type === 'String' &&
        attributes !== undefined &&
        attributes.find(
            (otherAttr: providers.EntityAttributeSchema) =>
                otherAttr.type === 'String' &&
                otherAttr.name === attribute.name + '_format'
        ) !== undefined
    );
}
